import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { fetchUserProfile } from "../../../actions/authAction/loginAction";
import authorizedAxios from "../../../helpers/authorizedAxios";
import { AlertConfirmModal } from "../../../components/Modals/AlertConfirmModal";
import { showAlert, showToast } from "../../../utils/helpers/ShowToast";
import { decodeQrisImageFile } from "../../../utils/helpers/qrisImage";
import { validateQrisPayload } from "../../../utils/helpers/qris";
import { resolveAssetUrl } from "../../../utils/helpers/storeSetting";
import { prepareImageFile, validateImageFile } from "../../../utils/helpers/imageUpload";
import { FieldFeedback, FormValidationNotice, RequiredMark } from "../../../components/FormValidation";
import AkasSelect from "../../../components/AkasSelect";
import useFormValidation from "../../../hooks/useFormValidation";
import { validationRules as rule } from "../../../utils/helpers/formValidation";
import "./outlet-page.css";

const EMPTY_FORM = {
  code: "",
  name: "",
  email: "",
  phone: "",
  address: "",
  timezone: "Asia/Jakarta",
  receipt_prefix: "AKS",
  qris_payload: "",
  primary_color: "#0B2D32",
  is_default: false,
  is_active: true,
  qris_image: null,
};

const formatIDR = (value) => new Intl.NumberFormat("id-ID", {
  style: "currency",
  currency: "IDR",
  maximumFractionDigits: 0,
}).format(Number(value || 0));

const OutletGlyph = ({ type = "store" }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    {type === "users" ? <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/></> : <><path d="M3 21h18"/><path d="M5 21V8l7-4 7 4v13"/><path d="M9 21v-6h6v6"/></>}
  </svg>
);

const OutletPage = () => {
  const dispatch = useDispatch();
  const [payload, setPayload] = useState({ outlets: [], active_count: 0, maximum: 1 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [qrisError, setQrisError] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [roles, setRoles] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [assignmentRoles, setAssignmentRoles] = useState({});
  const [canManageAssignments, setCanManageAssignments] = useState(false);
  const [outletToDeactivate, setOutletToDeactivate] = useState(null);
  const [deactivatingId, setDeactivatingId] = useState(null);
  const formRef = useRef(null);
  const outletSchema = useMemo(() => ({
    code: [rule.required("Kode outlet wajib diisi."), rule.maxLength(30), rule.pattern(/^[A-Za-z0-9_-]+$/, "Gunakan huruf, angka, tanda hubung, atau garis bawah.")],
    name: [rule.required("Nama outlet wajib diisi."), rule.maxLength(120)],
    email: [rule.email(), rule.maxLength(120)],
    phone: rule.maxLength(50),
    address: rule.maxLength(1000),
    receipt_prefix: [rule.maxLength(20), rule.pattern(/^[A-Za-z0-9_-]+$/, "Prefix hanya boleh berisi huruf, angka, tanda hubung, atau garis bawah.")],
    primary_color: rule.pattern(/^#[0-9A-Fa-f]{6}$/, "Warna outlet harus berupa kode HEX 6 digit."),
  }), []);
  const validation = useFormValidation({ values: form, schema: outletSchema, formId: "outlet", focusRoot: formRef });

  const loadOutlets = useCallback(async () => {
    setLoading(true);
    try {
      const response = await authorizedAxios.get("/outlets");
      setPayload(response?.data?.data || { outlets: [] });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadOutlets(); }, [loadOutlets]);

  const canAdd = payload.maximum < 0 || payload.active_count < payload.maximum;
  const limitLabel = payload.maximum < 0 ? "Tanpa batas" : `${payload.active_count}/${payload.maximum}`;

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setSelectedUsers([]);
    setAssignmentRoles({});
    setQrisError("");
    setShowForm(true);
  };

  const openEdit = async (outlet) => {
    setEditingId(outlet.id);
    setForm({ ...EMPTY_FORM, ...outlet, qris_payload: outlet.qris_payload || "", qris_image: null });
    setSelectedUsers([]);
    setQrisError("");
    setShowForm(true);

    try {
      const [detailResponse, accountResponse, roleResponse] = await Promise.all([
        authorizedAxios.get(`/outlets/${outlet.id}`),
        authorizedAxios.get("/users", { params: { limit: 100, page: 1 } }),
        authorizedAxios.get("/roles", { params: { limit: 100, page: 1 } }),
      ]);
      const detail = detailResponse?.data?.data;
      const rows = accountResponse?.data?.data?.data || [];
      const roleRows = roleResponse?.data?.data?.data || roleResponse?.data?.data || [];
      const assignedUsers = detail?.users || [];
      setAccounts(rows);
      setRoles(Array.isArray(roleRows) ? roleRows : []);
      setSelectedUsers(assignedUsers.filter((user) => user.is_active !== false).map((user) => Number(user.id)));
      setAssignmentRoles(assignedUsers.reduce((result, user) => ({ ...result, [user.id]: user.role_id }), {}));
      setCanManageAssignments(true);
    } catch {
      setAccounts([]);
      setRoles([]);
      setAssignmentRoles({});
      setCanManageAssignments(false);
    }
  };

  const updateField = (key, value) => setForm((previous) => ({ ...previous, [key]: value }));

  const handleQrisFile = async (file) => {
    if (!file) {
      updateField("qris_image", null);
      return;
    }
    setQrisError("");
    try {
      validateImageFile(file, { preset: "qris", label: "Gambar QRIS" });
      const compressedFile = await prepareImageFile(file, { preset: "qris", label: "Gambar QRIS" });
      updateField("qris_image", compressedFile);
      try {
        const payload = await decodeQrisImageFile(file);
        updateField("qris_payload", payload);
      } catch (scanError) {
        setQrisError(scanError?.message || "QRIS tidak dapat dibaca. Isi payload secara manual.");
      }
    } catch (error) {
      updateField("qris_image", null);
      setQrisError(error?.message || "Gambar QRIS tidak dapat diproses.");
    }
  };

  const formData = useMemo(() => {
    const data = new FormData();
    ["code", "name", "email", "phone", "address", "timezone", "receipt_prefix", "qris_payload", "primary_color"]
      .forEach((key) => data.append(key, form[key] ?? ""));
    data.append("is_default", form.is_default ? "1" : "0");
    data.append("is_active", form.is_active ? "1" : "0");
    if (form.qris_image instanceof File) data.append("qris_image", form.qris_image);
    return data;
  }, [form]);

  const qrisPreview = useMemo(
    () => form.qris_image instanceof File ? URL.createObjectURL(form.qris_image) : "",
    [form.qris_image]
  );

  useEffect(() => () => {
    if (qrisPreview) URL.revokeObjectURL(qrisPreview);
  }, [qrisPreview]);

  const save = async (event) => {
    event.preventDefault();
    if (!validation.validateAll()) return;
    setQrisError("");
    if (form.qris_payload) {
      try { validateQrisPayload(form.qris_payload); }
      catch (error) { setQrisError(error?.message || "Payload QRIS tidak valid."); return; }
    }

    setSaving(true);
    try {
      const request = editingId
        ? authorizedAxios.post(`/outlets/${editingId}`, formData)
        : authorizedAxios.post("/outlets", formData);
      await showToast(request, {
        loading: editingId ? "Memperbarui outlet..." : "Menambahkan outlet...",
        success: editingId ? "Outlet berhasil diperbarui" : "Outlet berhasil ditambahkan",
        error: (error) => error?.response?.data?.message || "Outlet gagal disimpan",
      });

      if (editingId && canManageAssignments) {
        const users = accounts.map((account) => ({
          user_id: account.id,
          role_id: Number(assignmentRoles[account.id] || account.role_id),
          is_active: selectedUsers.includes(Number(account.id)),
        }));
        await showToast(authorizedAxios.put(`/outlets/${editingId}/users`, { users }), {
          loading: "Menyimpan akses tim...",
          success: "Akses tim outlet berhasil diperbarui",
          error: (error) => error?.response?.data?.message || "Akses tim gagal diperbarui",
        });
      }

      setShowForm(false);
      await loadOutlets();
      await dispatch(fetchUserProfile());
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!outletToDeactivate || deactivatingId) return;

    setDeactivatingId(outletToDeactivate.id);
    try {
      const response = await showToast(authorizedAxios.delete(`/outlets/${outletToDeactivate.id}`), {
        loading: "Menonaktifkan outlet...",
        success: "Outlet berhasil dinonaktifkan",
        error: (error) => error?.response?.data?.message || "Outlet gagal dinonaktifkan",
      });
      showAlert({
        type: "success",
        message: response?.data?.message || `Outlet ${outletToDeactivate.name} berhasil dinonaktifkan.`,
      });
      setOutletToDeactivate(null);
      await loadOutlets();
      await dispatch(fetchUserProfile());
    } finally {
      setDeactivatingId(null);
    }
  };

  return (
    <div className="page-wrapper outlet-page">
      <div className="page-header d-print-none"><div className="container-xl"><div className="row align-items-center g-3">
        <div className="col"><div className="page-pretitle">OPERASIONAL MULTI OUTLET</div><h2 className="page-title">Outlet & Cabang</h2><p className="text-secondary mb-0">Kelola identitas, QRIS, tim, dan konteks transaksi setiap lokasi.</p></div>
        <div className="col-auto"><button className="btn btn-primary" type="button" disabled={!canAdd} onClick={openCreate}>+ Tambah Outlet</button></div>
      </div></div></div>

      <div className="page-body"><div className="container-xl">
        <div className="outlet-summary-grid">
          <div className="outlet-summary-card"><span><OutletGlyph /></span><div><small>Outlet aktif</small><strong>{payload.active_count || 0}</strong></div></div>
          <div className="outlet-summary-card"><span><OutletGlyph type="users" /></span><div><small>Kuota paket</small><strong>{limitLabel}</strong></div></div>
          <div className="outlet-summary-note"><strong>Data aman per lokasi</strong><p>Stok, transaksi, kas, QRIS, produksi, dan laporan mengikuti outlet aktif.</p></div>
        </div>

        {loading ? <div className="outlet-empty">Memuat outlet...</div> : (
          <div className="outlet-card-grid">
            {(payload.outlets || []).map((outlet) => (
              <article className={`outlet-card ${outlet.id === payload.current_outlet_id ? "is-current" : ""}`} key={outlet.id}>
                <div className="outlet-card-top"><span className="outlet-code">{outlet.code}</span><div className="d-flex gap-2">{outlet.is_default && <span className="badge bg-lime-lt">Utama</span>}{outlet.id === payload.current_outlet_id && <span className="badge bg-green-lt">Aktif</span>}</div></div>
                <div className="outlet-card-title"><span style={{ background: outlet.effective_primary_color }}><OutletGlyph /></span><div><h3>{outlet.name}</h3><p>{outlet.address || "Alamat belum diisi"}</p></div></div>
                <div className="outlet-meta outlet-operational-meta"><div><small>Omzet hari ini</small><strong>{formatIDR(outlet.metrics?.sales_today)}</strong></div><div><small>Transaksi hari ini</small><strong>{outlet.metrics?.transactions_today || 0}</strong></div><div><small>Stok menipis</small><strong className={outlet.metrics?.low_stock_items ? "text-warning" : ""}>{outlet.metrics?.low_stock_items || 0} item</strong></div><div><small>Transfer masuk</small><strong>{outlet.metrics?.incoming_transfers || 0}</strong></div><div><small>Tim</small><strong>{outlet.users_count || 0} user</strong></div><div><small>QRIS · {outlet.receipt_prefix}</small><strong>{outlet.effective_qris_payload ? "Siap" : "Belum"}</strong></div></div>
                <div className="outlet-card-actions"><button type="button" className="btn btn-primary" onClick={() => openEdit(outlet)}>Kelola outlet</button>{!outlet.is_default && <button type="button" className="btn btn-outline-danger" disabled={deactivatingId === outlet.id} data-bs-toggle="modal" data-bs-target="#modal-alert-deactivate-outlet" onClick={() => setOutletToDeactivate(outlet)}>{deactivatingId === outlet.id ? "Menonaktifkan..." : "Nonaktifkan"}</button>}</div>
              </article>
            ))}
          </div>
        )}
      </div></div>

      {showForm && <div className="outlet-modal-backdrop" role="presentation"><div className="outlet-modal" role="dialog" aria-modal="true" aria-labelledby="outlet-form-title">
        <form ref={formRef} onSubmit={save} noValidate>
          <div className="outlet-modal-header"><div><small>KONFIGURASI LOKASI</small><h3 id="outlet-form-title">{editingId ? "Perbarui outlet" : "Outlet baru"}</h3></div><button type="button" className="btn-close" aria-label="Tutup" onClick={() => setShowForm(false)} /></div>
          <div className="outlet-modal-body">
            <FormValidationNotice errors={validation.errors} />
            <section><h4>Identitas outlet</h4><div className="row g-3">
              <div className="col-md-4"><label className="form-label">Kode outlet<RequiredMark /></label><input {...validation.fieldProps("code")} className={validation.inputClass("code")} maxLength="30" value={form.code} onChange={(e) => updateField("code", e.target.value.toUpperCase())} placeholder="JKT01" /><FieldFeedback formId="outlet" name="code" error={validation.errors.code} hint="Unik, maksimal 30 karakter tanpa spasi." /></div>
              <div className="col-md-8"><label className="form-label">Nama outlet<RequiredMark /></label><input {...validation.fieldProps("name")} className={validation.inputClass("name")} maxLength="120" value={form.name} onChange={(e) => updateField("name", e.target.value)} placeholder="Akas Coffee Kemang" /><FieldFeedback formId="outlet" name="name" error={validation.errors.name} hint="Maksimal 120 karakter." /></div>
              <div className="col-md-6"><label className="form-label">Email<RequiredMark optional /></label><input {...validation.fieldProps("email")} type="email" className={validation.inputClass("email")} maxLength="120" value={form.email || ""} onChange={(e) => updateField("email", e.target.value)} /><FieldFeedback formId="outlet" name="email" error={validation.errors.email} hint="Jika diisi, gunakan email yang valid." /></div>
              <div className="col-md-6"><label className="form-label">Nomor telepon<RequiredMark optional /></label><input {...validation.fieldProps("phone")} className={validation.inputClass("phone")} maxLength="50" value={form.phone || ""} onChange={(e) => updateField("phone", e.target.value)} /><FieldFeedback formId="outlet" name="phone" error={validation.errors.phone} hint="Maksimal 50 karakter." /></div>
              <div className="col-12"><label className="form-label">Alamat<RequiredMark optional /></label><textarea {...validation.fieldProps("address")} className={validation.inputClass("address")} maxLength="1000" rows="2" value={form.address || ""} onChange={(e) => updateField("address", e.target.value)} /><FieldFeedback formId="outlet" name="address" error={validation.errors.address} hint="Maksimal 1.000 karakter." /></div>
              <div className="col-md-6"><label className="form-label">Prefix nomor struk<RequiredMark optional /></label><input {...validation.fieldProps("receipt_prefix")} className={validation.inputClass("receipt_prefix")} maxLength="20" value={form.receipt_prefix || ""} onChange={(e) => updateField("receipt_prefix", e.target.value.toUpperCase())} /><FieldFeedback formId="outlet" name="receipt_prefix" error={validation.errors.receipt_prefix} hint="Maksimal 20 karakter tanpa spasi." /></div>
              <div className="col-md-6"><label className="form-label">Warna outlet<RequiredMark optional /></label><div className="outlet-color-input"><input type="color" value={form.primary_color || "#0B2D32"} onChange={(e) => updateField("primary_color", e.target.value.toUpperCase())} /><input {...validation.fieldProps("primary_color")} className={validation.inputClass("primary_color")} maxLength="7" value={form.primary_color || ""} onChange={(e) => updateField("primary_color", e.target.value.toUpperCase())} /></div><FieldFeedback formId="outlet" name="primary_color" error={validation.errors.primary_color} hint="Contoh: #0B2D32." /></div>
            </div></section>

            <section><h4>QRIS outlet</h4><div className="qris-config-grid"><div className="qris-config-preview">{qrisPreview ? <img src={qrisPreview} alt="Preview QRIS" /> : form.effective_qris_image_path ? <img src={resolveAssetUrl(form.effective_qris_image_path, "")} alt="QRIS outlet" /> : <span>QRIS</span>}</div><div><input type="file" className="form-control" accept="image/png,image/jpeg,image/webp" onChange={(e) => handleQrisFile(e.target.files?.[0])} /><div className="form-hint">Maksimal 4 MB. Payload dibaca otomatis dan disimpan khusus outlet ini.</div><textarea className="form-control mt-2" rows="3" value={form.qris_payload || ""} onChange={(e) => updateField("qris_payload", e.target.value.trim())} placeholder="Payload QRIS statis" />{qrisError && <div className="text-danger small mt-2">{qrisError}</div>}</div></div></section>

            {editingId && canManageAssignments && <section><h4>Tim & peran di outlet ini</h4><div className="outlet-user-grid">{accounts.map((account) => { const isSelected = selectedUsers.includes(Number(account.id)); return <div key={account.id} className={`outlet-user-assignment ${isSelected ? "is-selected" : ""}`}><label><input type="checkbox" checked={isSelected} onChange={(e) => setSelectedUsers((current) => e.target.checked ? [...current, Number(account.id)] : current.filter((id) => id !== Number(account.id)))} /><span>{account.name}<small>{account.email || account.username}</small></span></label>{isSelected && <AkasSelect className="form-select form-select-sm" aria-label={`Peran ${account.name}`} value={assignmentRoles[account.id] || account.role_id || ""} onChange={(e) => setAssignmentRoles((current) => ({ ...current, [account.id]: e.target.value }))}>{roles.map((role) => <option key={role.id} value={role.id}>{role.name}</option>)}</AkasSelect>}</div>; })}</div><div className="form-hint mt-2">Permission mengikuti peran pada outlet aktif. Owner dan Super Admin tetap memiliki akses penuh.</div></section>}

            <section className="outlet-form-switches"><label><input type="checkbox" checked={Boolean(form.is_default)} onChange={(e) => updateField("is_default", e.target.checked)} /><span><strong>Jadikan outlet utama</strong><small>Menjadi fallback ketika user belum memilih outlet.</small></span></label><label><input type="checkbox" checked={Boolean(form.is_active)} onChange={(e) => updateField("is_active", e.target.checked)} /><span><strong>Outlet aktif</strong><small>Dapat dipilih dan menerima transaksi.</small></span></label></section>
          </div>
          <div className="outlet-modal-footer"><button type="button" className="btn btn-link" onClick={() => setShowForm(false)}>Batal</button><button type="submit" className="btn btn-primary" disabled={saving}>{saving ? "Menyimpan..." : "Simpan outlet"}</button></div>
        </form>
      </div></div>}

      <AlertConfirmModal
        type="warning"
        title="Nonaktifkan outlet?"
        message={outletToDeactivate
          ? `Outlet ${outletToDeactivate.name} tidak dapat menerima transaksi baru setelah dinonaktifkan. Seluruh histori transaksi, stok, dan laporan tetap tersimpan.`
          : "Outlet akan dinonaktifkan dan seluruh histori tetap tersimpan."}
        confirmText="Nonaktifkan"
        cancelText="Kembali"
        idModal="alert-deactivate-outlet"
        onConfirm={remove}
        onCancel={() => setOutletToDeactivate(null)}
      />
    </div>
  );
};

export default OutletPage;
